import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useMemoizedSelector } from '@/hooks/use-memoized-selector';
import { makeSelectEventById } from '@/models/event';
import classnames from 'classnames';
import Text, { ITextSizes, ITextColors, ITextStyles } from '@/components/ui/content/Text';
import Heading, { IHeadingColors, IHeadingSizes, IUnderlineTypes } from '@/components/ui/content/Heading';
import CircularProgressBar, { IPathColorTypes } from '@/components/ui/CircularProgressBar';
import { ILiveEventColorThemes } from '@/types';
import styles from './styles.module.scss';
import { ICountdownTimerRoundProps } from './config';

export const getTimerObject = secs => {
  if (secs <= 0) {
    return {
      minutes: 0, seconds: 0
    };
  }
  const minutes = Math.floor(secs / 60);
  const seconds = secs % 60;

  return { minutes, seconds };
}

export const calculateSecondsLeftFromTimer = ({ minutes, seconds }) => {
  return minutes * 60 + seconds;
}

const MaxSecondsAllowed = 5999; // 99 minutes and 99 seconds

const CountdownTimerRound = forwardRef((props: ICountdownTimerRoundProps, ref) => {
  const { defaultSecondsLeft, onComplete, config = {}, matchedCount } = props;
  const { autoStart = true, dangerWhenLessThan = 0, blinkWhenDanger = false } = config;
  const secondsLeft = Math.min(MaxSecondsAllowed, defaultSecondsLeft); // || (new Date(countdownTil).getTime() - Date.now()) / 1000);
  const initialMount = useRef(true);
  const timerIntervalRef = useRef();
  const [timer, setTimer] = useState(getTimerObject(0));
  const { eventId = '' } = useParams();
  const event = useMemoizedSelector(makeSelectEventById, eventId);

  const isDarkGradient = event.colorTheme ? event.colorTheme === ILiveEventColorThemes.DARK : false;

  useImperativeHandle(ref, () => ({
    getRemainingTimer() {
      return timer;
    },
    getRemainingSeconds() {
      return calculateSecondsLeftFromTimer(timer);
    },
    startNow() {
      startTimer();
      return;
    },
    resetTimer() {
      setTimer(getTimerObject(secondsLeft));
      return;
    }
  }));

  useEffect(() => {
    if (!defaultSecondsLeft) {
      return;
    }

    if (secondsLeft <= 0) {
      setTimer(getTimerObject(0));
      return;
    }

    setTimer(getTimerObject(secondsLeft));

    return () => {
      timerIntervalRef.current && clearInterval(timerIntervalRef.current);
      return;
    }

  }, [defaultSecondsLeft]);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }
    const _secondsLeft = calculateSecondsLeftFromTimer(timer);
    if (_secondsLeft > 0 && autoStart && !timerIntervalRef.current) {
      startTimer();
      return;
    }
    else if (_secondsLeft <= 0) {
      timerIntervalRef.current && clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = undefined;
      if (!initialMount.current) {
        onComplete && onComplete();
      }
    }

  }, [timer.minutes, timer.seconds])

  const startTimer = () => {
    if (timerIntervalRef.current) { return; }
    const _intervalId = setInterval(decrementTimer, 1000);
    timerIntervalRef.current = _intervalId;
    return;
  }

  const decrementTimer = () => {
    setTimer(prevTimer => getTimerObject(calculateSecondsLeftFromTimer(prevTimer) - 1));
    return;
  }

  const totalSecondsLeft = calculateSecondsLeftFromTimer(timer);
  const showDanger = dangerWhenLessThan > 0 && totalSecondsLeft > 0 && totalSecondsLeft <= dangerWhenLessThan;

  const timeLeftDisplay = (text, className) => <Heading className={classnames(className, { [styles.timeLeftBlink]: showDanger && blinkWhenDanger })} text={text} headingSize={IHeadingSizes.MEDIUM} headingColor={IHeadingColors.NEUTRAL_DARK} />
  return (
    <div className={styles.container}>
      <div className={styles.progress}>
        <CircularProgressBar strokeWidth={2} valuePercent={Math.floor((totalSecondsLeft / defaultSecondsLeft) * 100)} color={showDanger ? IPathColorTypes.DANGER : IPathColorTypes.PRIMARY}>
          <div className={classnames(styles.timer, { [styles.timerDarkThemeBg]: isDarkGradient, [styles.timerLightThemeBg]: !isDarkGradient })}>
            <div className={styles.timeLeftTextWrapper}>
              <Text text="TIME LEFT" textSize={ITextSizes.XSMALL} textColor={ITextColors.CONTRAST} textStyle={ITextStyles.BOLDED} />
            </div>
            <div className={styles.timeLeftWrapper}>
              {
                timeLeftDisplay(`${timer.minutes}`.padStart(2, '0'), styles.minutes)
              }
              {
                timeLeftDisplay(':', styles.separator)
              }
              {
                timeLeftDisplay(`${timer.seconds}`.padStart(2, '0'), styles.seconds)
              }
            </div>
            <div className={styles.matchedInfoText}>
              <Text text="matched with" textSize={ITextSizes.SMALL} textColor={ITextColors.NEUTRAL_DARK} />
              <Text text={`${matchedCount} Professional${matchedCount === 1 ? '' : 's'}`} textSize={ITextSizes.MEDIUM} textColor={ITextColors.NEUTRAL_DARK} textStyle={ITextStyles.BOLDED} />
              <Text text="till now" textSize={ITextSizes.SMALL} textColor={ITextColors.NEUTRAL_DARK} />
            </div>
          </div>
        </CircularProgressBar>
      </div>
    </div>
  )
});

export default React.memo(CountdownTimerRound);
